// AltitudeSlider — right-hand vertical slider for the shared altitude
// reference state used by terrain advisor + future altitude-aware
// layers (GFA icing/turb altitude band filter, freezing level, etc.).
//
// Pulls value from uiStore.altitudeSliderFt; renders a vertical input
// pinned to the right edge of the map area. When `altitudeSliderLocked`
// is true, the slider passively tracks the live GPS altitude (live
// altitude in feet MSL) and the user can't drag it; when unlocked
// the user is free to drag it to ask 'what would hazards look like at
// this altitude?'.
//
// Range: SFC (0 ft) to FL250 (25,000 ft). Quantized to 100 ft steps.
// Cap was previously 45,000 ft; lowered 2026-05-24 because there is no
// terrain above ~29,000 ft on Earth (Mt. Everest) and the terrain
// advisor isn't useful above 25k — the slider drag range was wasted
// on altitudes where every pixel reads 'unrestricted'.
//
// Visibility: shown only when at least one altitude-aware layer is
// on (terrain advisor today; more in future). The host (MapPage)
// gates rendering by passing `visible` from a derived selector.
//
// Layout: vertically centered, right-edge anchored. Stays out of the
// way of the right-rail FAB stack (which lives at right-3 bottom-24)
// and the AlertCenter (top-3 right-3 ish) by sitting in the middle.

import { useEffect } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useGpsStore } from '../stores/gpsStore';
import { Lock, Unlock } from '../shell/Icon';

const M_TO_FT = 3.28084;

const SLIDER_MIN_FT = 0;
const SLIDER_MAX_FT = 25_000;
const SLIDER_STEP_FT = 100;

interface Props {
  /** Whether the slider should render. Driven by host (MapPage)
   *  based on whether any altitude-aware layer is active. */
  visible: boolean;
  /**
   * Optional terrain elevation (ft MSL) at the GPS fix. When set
   *  AND the slider is locked to GPS, an 'AGL' readout shows below
   *  the MSL value so the pilot sees true terrain clearance. */
  terrainElevFt?: number | null;
}

export default function AltitudeSlider({ visible, terrainElevFt }: Props) {
  const ft = useUiStore((s) => s.altitudeSliderFt);
  const locked = useUiStore((s) => s.altitudeSliderLocked);
  const setFt = useUiStore((s) => s.setAltitudeSliderFt);
  const setLocked = useUiStore((s) => s.setAltitudeSliderLocked);
  const fix = useGpsStore((s) => s.fix);

  // Lock-to-GPS: when locked AND a fix is available, write the live
  // altitude into the slider so it tracks the aircraft. Skipping the
  // setFt call when the value hasn't changed by at least 50 ft keeps
  // re-renders from cycling on every micro-fluctuation.
  useEffect(() => {
    if (!locked || !fix || fix.altMeters == null) return;
    const liveFt = fix.altMeters * M_TO_FT;
    if (Math.abs(liveFt - ft) >= 50) {
      setFt(liveFt);
    }
  }, [locked, fix?.altMeters, ft, setFt]);

  if (!visible) return null;

  const aglFt = terrainElevFt != null ? Math.round(ft - terrainElevFt) : null;

  return (
    <div
      // Right edge, vertically centered. Out of the way of:
      //   - top-right AlertCenter (top-4-ish)
      //   - right-rail FAB stack (bottom-24)
      //   - bottom bars (bottom-3..bottom-32)
      className="absolute z-20 right-3 flex flex-col items-center pointer-events-none"
      style={{
        // Vertically centered with a generous height; viewport-clamped
        // so it doesn't run off the screen on shorter mobile.
        top: '50%',
        transform: 'translateY(-50%)',
      }}
    >
      <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar p-2 flex flex-col items-center gap-2">
        {/* MSL readout */}
        <div className="text-center min-w-[3.5rem]">
          <div className="text-[9px] tracking-widest text-cockpit-400 uppercase">MSL</div>
          <div className="font-mono text-[12px] text-ember-300 leading-tight">
            {Math.round(ft).toLocaleString()}′
          </div>
          {aglFt != null && (
            <>
              <div className="text-[9px] tracking-widest text-cockpit-400 uppercase mt-0.5">AGL</div>
              <div className={[
                'font-mono text-[12px] leading-tight',
                aglFt < 200 ? 'text-red-400' : aglFt < 500 ? 'text-amber-300' : 'text-cockpit-100',
              ].join(' ')}>
                {aglFt.toLocaleString()}′
              </div>
            </>
          )}
        </div>

        {/* Vertical slider. We rotate a horizontal input -90deg so it
            inherits MapLibre + Tailwind styling cleanly. Height of
            the rotated track = the input's `width` style. */}
        <div
          className="flex items-center justify-center"
          style={{ height: 220, width: 28 }}
        >
          <input
            type="range"
            min={SLIDER_MIN_FT}
            max={SLIDER_MAX_FT}
            step={SLIDER_STEP_FT}
            value={Math.round(ft)}
            // Rotation: -90deg for a vertical slider. Width becomes
            // the visual height; height stays slim.
            style={{
              width: 220,
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
            }}
            disabled={locked}
            onChange={(e) => setFt(parseFloat(e.currentTarget.value))}
            aria-label="Altitude reference (ft MSL)"
            className="accent-ember-500"
          />
        </div>

        {/* Lock-to-GPS toggle */}
        <button
          onClick={() => setLocked(!locked)}
          className={[
            'flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] tracking-wide transition-colors',
            locked
              ? 'bg-ember-600/25 border-ember-500 text-ember-200'
              : 'bg-cockpit-700 border-cockpit-600 text-cockpit-200',
          ].join(' ')}
          aria-label={locked ? 'Unlock altitude (drag manually)' : 'Lock altitude to GPS'}
          title={locked ? 'Currently following GPS altitude. Click to drag manually.' : 'Currently free-drag. Click to track GPS altitude.'}
        >
          {locked
            ? <Lock className="h-3 w-3" strokeWidth={2.2} />
            : <Unlock className="h-3 w-3" strokeWidth={2.2} />}
          {locked ? 'GPS' : 'FREE'}
        </button>

        {/* Min/max scale labels for orientation */}
        <div className="text-[8px] tracking-widest text-cockpit-400 leading-none mt-1">
          0→FL250
        </div>
      </div>
    </div>
  );
}

